import React from 'react'
import { useTranslation } from 'react-i18next'
import TableHeaders from '../../Interfaces/TableHeaders'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { ProgramDirectoryReportRow } from '../../../swagger'
import { Box } from '@mui/system'
import { TableHeaderForPDF } from '../../Table/TableHeaderForPDF'
import { styles } from './styles'
import { dateToDashString } from '../../../utils/dateUtility'

const widthForName = '9%'
const widthForEmail = '17%'
const widthForPhone = '11%'
const widthForStudent = '10%'
const widthForBirthday = '9%'
const widthForStatus = '9%'
const widthForDate = '9%'

export const formatBirthday = (month?: number, year?: number): string => {
  if (month && year) {
    return `${month}/${year}`
  }
  return year ? `${year}` : ''
}

/**
 * Guards against empty dates.
 */
export const formatReportDate = (date?: Date): string => {
  if (!date || isNaN(new Date(date).getTime())) {
    return ''
  }
  return dateToDashString(date)
}

const ProgramDirectoryPDF: React.FC<{
  rows: ProgramDirectoryReportRow[]
  communityName?: string
}> = ({ rows, communityName }) => {
  const { t } = useTranslation()

  const tableHeaders: TableHeaders[] = [
    {
      label: t(
        'ProgramDirectoryPDF.Table.Header.ParentFirstName',
        'Parent First Name'
      ),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForName },
    },
    {
      label: t(
        'ProgramDirectoryPDF.Table.Header.ParentLastName',
        'Parent Last Name'
      ),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForName },
    },
    {
      label: t('ProgramDirectoryPDF.Table.Header.Email', 'Email'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForEmail },
    },
    {
      label: t('ProgramDirectoryPDF.Table.Header.Phone', 'Phone'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForPhone },
    },
    {
      label: t('ProgramDirectoryPDF.Table.Header.StudentName', 'Student Name'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForStudent },
    },
    {
      label: t(
        'ProgramDirectoryPDF.Table.Header.StudentBirthday',
        'Birth Month/Year'
      ),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForBirthday },
    },
    {
      label: t('ProgramDirectoryPDF.Table.Header.Status', 'Status'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForStatus },
    },
    {
      label: t('ProgramDirectoryPDF.Table.Header.StatusDate', 'Status Date'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForDate },
    },
    {
      label: t(
        'ProgramDirectoryPDF.Table.Header.EnrolledDate',
        'Enrollment Date'
      ),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForDate },
    },
  ]

  return (
    <Box>
      <div style={styles.familyInfo}>
        <Typography
          style={styles.title}
          variant="subtitle1"
          component="h5"
          justifyContent={'flex-start'}
        >
          {communityName
            ? t(
                'ProgramDirectoryPDF.Section.Title',
                '{{communityName}} Family Directory',
                {
                  communityName,
                }
              )
            : t(
                'ProgramDirectoryPDF.Section.TitleFallback',
                'Family Directory'
              )}
        </Typography>

        <div style={styles.familyMetaRow}>
          {t('ProgramDirectoryPDF.Section.TotalEnrollments', 'Enrollments')}:{' '}
          {rows.length}
        </div>
      </div>
      <Box sx={styles.containerTable}>
        <Table
          aria-labelledby={'program-directory-table-for-pdf'}
          style={styles.table}
        >
          <TableHead>
            <TableHeaderForPDF tableHeaders={tableHeaders} />
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForName }}
                >
                  {row.parentFirstName}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForName }}
                >
                  {row.parentLastName}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForEmail }}
                >
                  {row.parentEmail}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForPhone }}
                >
                  {row.parentPhone}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForStudent }}
                >
                  {row.studentFirstName}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForBirthday }}
                >
                  {formatBirthday(row.studentBirthMonth, row.studentBirthYear)}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForStatus }}
                >
                  {row.enrollmentStatus}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForDate }}
                >
                  {formatReportDate(row.enrollmentStatusDate)}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForDate }}
                >
                  {formatReportDate(row.enrolledDate)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}

export default ProgramDirectoryPDF
